import moment from "moment";

const parseMetadata = (metadata) => {
  if (!metadata) return null;
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return null;
  }
};

const getField = (obj, camelKey, snakeKey) =>
  obj?.[camelKey] ?? obj?.[snakeKey];

const parseRows = (rowsValue) => {
  if (rowsValue == null) return [];
  if (Array.isArray(rowsValue)) return rowsValue;
  if (typeof rowsValue === "string") {
    try {
      const parsed = JSON.parse(rowsValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const renderRowsTable = (rows) => {
  if (!rows.length) {
    return <div className="opacity-70 text-sm mt-2">No rows returned.</div>;
  }

  const firstRow = rows[0];
  const isObjectRow =
    firstRow && typeof firstRow === "object" && !Array.isArray(firstRow);
  const columns = isObjectRow ? Object.keys(firstRow) : ["value"];

  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full border-collapse bg-gray-50 dark:bg-gray-800">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="border border-gray-100 dark:border-gray-700 px-2 py-1 text-left"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const normalizedRow =
              row && typeof row === "object" && !Array.isArray(row)
                ? row
                : { value: row };
            return (
              <tr key={index}>
                {columns.map((column) => (
                  <td
                    key={column}
                    className="border border-gray-100 dark:border-gray-700 px-2 py-1 align-top"
                  >
                    {toText(normalizedRow?.[column])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const MessageView = ({
  message,
  onSqlDetailsToggle,
  isLastMessage = false,
}) => {
  const metadata = parseMetadata(message.metadata);
  const isAssistant = message.role === "assistant";

  const renderAssistantMessage = () => {
    const questionType =
      getField(metadata, "questionType", "question_type") || null;
    const unsupported =
      Boolean(metadata?.unsupported) || questionType === "unsupported";
    const errorText =
      metadata?.error ||
      (metadata?.success === false && !unsupported ? message.content : null);
    const reason = metadata?.reason;
    const rows = parseRows(metadata?.rows);
    const statusText = metadata?.answer || message.content;
    const sql = metadata?.sql;

    const sqlDetails = sql ? (
      <details
        className="group mt-2 text-xs"
        onToggle={(event) => onSqlDetailsToggle?.(event, isLastMessage)}
      >
        <summary className="cursor-pointer opacity-70">
          View SQL details
        </summary>
        <div className="pt-1">
          <pre className="rounded-xl mt-2 p-4 border border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-pre-wrap break-words font-code">
            {sql}
          </pre>
        </div>
      </details>
    ) : null;

    if (errorText) {
      return <div className="text-red-500 text-sm">{toText(errorText)}</div>;
    }

    if (unsupported) {
      return (
        <div className="text-sm">
          {toText(reason) || toText(statusText) || "This question is unsupported."}
        </div>
      );
    }

    if (rows.length > 0 || questionType === "data") {
      return (
        <>
          {statusText ? <div className="text-sm">{toText(statusText)}</div> : null}
          {renderRowsTable(rows)}
          {sqlDetails}
        </>
      );
    }

    return (
      <>
        <div className="text-sm">{toText(statusText || message.content || "")}</div>
        {sqlDetails}
      </>
    );
  };

  return (
    <div className="py-6">
      <div className="opacity-50 mb-1">
        {moment(message.createdAt).format("YYYY-MM-DD HH:mm:ss")}
      </div>
      <div className="flex items-start gap-2">
        <div className="font-semibold shrink-0">{isAssistant ? "A" : "Q"}:</div>
        <div className="min-w-0 flex-1">
          {isAssistant ? (
            renderAssistantMessage()
          ) : (
            <div>{toText(message.content)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageView;
